import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import logo from '../../assets/images/logo.png'
import Footer from '../../components/layout/Footer'

const LegalPageLayout = ({ title, lastUpdated, content, metaDescription }) => {
  useEffect(() => {
    window.scrollTo(0, 0)
    document.title = `${title} - EasyAppIcon`
    
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', metaDescription)
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) {
      ogTitle.setAttribute('content', `${title} - EasyAppIcon`)
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]')
    if (ogDescription) {
      ogDescription.setAttribute('content', metaDescription)
    }
    
    return () => {
      document.title = 'EasyAppIcon - Create Mobile App Icons'
    }
  }, [title, metaDescription])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-200 mb-6 font-medium group"
          >
            <span className="group-hover:-translate-x-1 transition-transform duration-200">←</span>
            <span>Back to Home</span>
          </Link>

          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="p-2 bg-[#df6e57] rounded-xl shadow-lg">
                <img src={logo} alt="EasyAppIcon Logo" className="w-8 h-8" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">EasyAppIcon</h1>
            </div>
          </div>
        </motion.div>

        {/* Markdown Content */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="p-8 md:p-12">
            <article className="legal-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  // Main title (h1)
                  h1: ({ children }) => (
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-primary-500">
                      {children}
                    </h1>
                  ),
                  // Section titles (h2)
                  h2: ({ children }) => (
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-10 mb-4 pb-2 border-b border-gray-200 flex items-center">
                      <span className="w-1 h-6 bg-primary-500 rounded-full mr-3"></span>
                      {children}
                    </h2>
                  ),
                  // Subsection titles (h3)
                  h3: ({ children }) => (
                    <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
                      {children}
                    </h3>
                  ),
                  // Paragraphs
                  p: ({ children }) => (
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {children}
                    </p>
                  ),
                  // Strong/Bold text
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-900">
                      {children}
                    </strong>
                  ),
                  // Emphasis/Italic
                  em: ({ children }) => (
                    <em className="text-gray-600 italic">
                      {children}
                    </em>
                  ),
                  // Links
                  a: ({ href, children }) => {
                    if (href && (href.startsWith('/') || href.startsWith('#'))) {
                      return (
                        <Link 
                          to={href} 
                          className="text-primary-600 hover:text-primary-700 font-medium underline decoration-primary-300 hover:decoration-primary-500 underline-offset-2 transition-colors duration-200"
                        >
                          {children}
                        </Link>
                      )
                    }
                    return (
                      <a 
                        href={href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 font-medium underline decoration-primary-300 hover:decoration-primary-500 underline-offset-2 transition-colors duration-200"
                      >
                        {children}
                      </a>
                    )
                  },
                  // Unordered lists
                  ul: ({ children }) => (
                    <ul className="my-4 space-y-2 pl-1">
                      {children}
                    </ul>
                  ),
                  // Ordered lists
                  ol: ({ children }) => (
                    <ol className="my-4 space-y-2 pl-1 list-decimal list-inside">
                      {children}
                    </ol>
                  ),
                  // List items
                  li: ({ children }) => (
                    <li className="text-gray-700 leading-relaxed flex items-start">
                      <span className="text-primary-500 mr-3 mt-1.5 flex-shrink-0">•</span>
                      <span>{children}</span>
                    </li>
                  ),
                  // Blockquotes
                  blockquote: ({ children }) => (
                    <blockquote className="my-6 pl-4 border-l-4 border-amber-400 bg-amber-50 py-4 pr-4 rounded-r-lg">
                      <div className="text-amber-900">
                        {children}
                      </div>
                    </blockquote>
                  ),
                  // Code inline
                  code: ({ children, inline }) => {
                    if (inline) {
                      return (
                        <code className="bg-gray-100 text-primary-700 px-1.5 py-0.5 rounded text-sm font-mono">
                          {children}
                        </code>
                      )
                    }
                    return (
                      <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-4">
                        {children}
                      </code>
                    )
                  },
                  // Horizontal rule
                  hr: () => (
                    <hr className="my-8 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                  ),
                  // Tables
                  table: ({ children }) => (
                    <div className="my-6 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="bg-white divide-y divide-gray-100">
                      {children}
                    </tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="hover:bg-gray-50 transition-colors duration-150">
                      {children}
                    </tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {children}
                    </td>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          </div>
        </motion.div>

        {/* Footer note */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="inline-flex items-center space-x-2 bg-white px-6 py-3 rounded-full shadow-md border border-gray-100">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-gray-600 text-sm">
              Last updated: <span className="font-medium text-gray-900">{lastUpdated}</span>
            </p>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  )
}

export default LegalPageLayout
